/**
 * =============================================================================
 * D-web-1 — TOKEN CUSTODY AND THE SINGLE-FLIGHT REFRESH MUTEX
 * =============================================================================
 *
 * DECISION (recorded as D-web-1, Phase 2 brief Part B / B2):
 *   · the ACCESS token lives in memory only — never localStorage, never a cookie,
 *     so it cannot be read by anything that survives a tab close;
 *   · the REFRESH token lives in localStorage. This is an internal tool behind a
 *     login, and the alternative (an httpOnly cookie) would need a same-site
 *     server the API is not; the trade is recorded, not hidden;
 *   · exactly ONE refresh may ever be in flight.
 *
 * -----------------------------------------------------------------------------
 * WHY THE MUTEX IS NOT AN OPTIMISATION. IT IS THE DIFFERENCE BETWEEN A WORKING
 * DASHBOARD AND A PILOT USER BEING LOGGED OUT.
 * -----------------------------------------------------------------------------
 * Phase 0's refresh is **revoke-on-rotate**: every successful refresh issues a
 * new refresh token and invalidates the presented one, and presenting an ALREADY
 * ROTATED token is treated as THEFT — the whole token chain is revoked and the
 * user is signed out everywhere.
 *
 * The dashboard mounts three independent queries at once: stage-counts, the
 * inquiry list, and an SSE ticket mint. If the access token has expired, all
 * three come back 401 within milliseconds of each other. A naive
 * "on 401, refresh and retry" interceptor then fires THREE refreshes with the
 * SAME refresh token. The first rotates it; the second and third present a
 * rotated token; the server — correctly — concludes the token was stolen and
 * revokes the chain. The user is bounced to the login screen for the crime of
 * opening the dashboard. React 18's StrictMode double-mounting makes this fire
 * on the very first render in development.
 *
 * So: `refreshInFlight` is a module-level promise. Whoever finds it non-null
 * AWAITS it instead of starting another. That alone is not sufficient — a 401
 * that arrives just AFTER a refresh settled would find `refreshInFlight` back at
 * null and start a second refresh with a token that has already been rotated.
 * `tokenGeneration` closes that hole: a request records the generation it was
 * sent under, and a refresh is only attempted if the generation has not already
 * moved. If it has, the caller simply retries with the token it now finds.
 *
 * The two guards together give the invariant the phase gate asked for:
 *   **N concurrent 401s ⇒ exactly ONE POST /auth/refresh.**
 * `src/lib/api.refresh-mutex.test.ts` asserts it with three concurrent 401s.
 *
 * A FAILED refresh clears all state and notifies the session listener, which
 * routes to the login screen. It does not retry: if the refresh token is dead,
 * every retry is another theft signal.
 */

import type { ApiError, ApiErrorCode } from './permissions';

/* ─────────────────────────────────────────────────────────── configuration ── */

const REFRESH_STORAGE_KEY = 'crf.refresh';

/**
 * The API base.
 *
 * Defaults to the same-origin path `/api/v1`, which Vite proxies to
 * `http://127.0.0.1:3000` in development (`vite.config.ts`). That default is the
 * safe one: no cross-origin request means the API's CORS allowlist — which holds
 * `http://localhost:5173` and therefore refuses `http://127.0.0.1:5173` — cannot
 * silently break the app depending on which hostname the developer typed.
 */
export const API_BASE_URL: string = import.meta.env.VITE_API_URL ?? '/api/v1';

/* ────────────────────────────────────────────────────────────────── errors ── */

/** A refusal the API described in the spec §10 envelope `{error:{code,message,details?}}`. */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode | string;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/** A transport failure — the request never got an answer (server down, DNS, offline). */
export class ApiTransportError extends Error {
  override readonly cause: unknown;
  constructor(message: string, cause: unknown) {
    super(message);
    this.name = 'ApiTransportError';
    this.cause = cause;
  }
}

function isApiErrorEnvelope(value: unknown): value is ApiError {
  if (typeof value !== 'object' || value === null) return false;
  const error = (value as { error?: unknown }).error;
  if (typeof error !== 'object' || error === null) return false;
  const { code, message } = error as { code?: unknown; message?: unknown };
  return typeof code === 'string' && typeof message === 'string';
}

/* ───────────────────────────────────────────────────────── session storage ── */

/** Access token — MEMORY ONLY. Reload loses it on purpose; the refresh rebuilds it. */
let accessToken: string | null = null;

/**
 * Monotonic counter, bumped on every credential change (login, refresh, logout).
 *
 * A request captures this before it is sent. On a 401 it asks "has the generation
 * moved since I was sent?" — if yes, somebody else already refreshed and this
 * request must simply retry, NOT refresh again with a rotated token.
 */
let tokenGeneration = 0;

/** THE MUTEX. Non-null exactly while a `POST /auth/refresh` is outstanding. */
let refreshInFlight: Promise<string> | null = null;

export type SessionExpiredListener = () => void;
let onSessionExpired: SessionExpiredListener | null = null;

/**
 * Register the "the refresh chain is gone, go to login" callback. The session
 * provider owns it; there is one, because there is one router.
 */
export function setSessionExpiredListener(listener: SessionExpiredListener | null): void {
  onSessionExpired = listener;
}

/**
 * Phase 2.5b — D7. Fired when ANY response comes back 403 `PREMISES_RESTRICTED`.
 *
 * Same shape as the session-expired listener above, for the same reason: the
 * refusal is not about one query — the premises guard sits in front of every
 * human route, so the FIRST such 403 means every subsequent request will answer
 * the same way until the user moves networks or signs out. A per-call-site
 * branch (the way `COUNTRY_SCOPE_VIOLATION` is handled, in the screens that can
 * name the country) would leave every other screen showing a generic error for
 * what is really a whole-session state. The session provider registers exactly
 * one listener and swaps the app for the blocked screen.
 *
 * The notification fires from `toResult` — the one funnel every response passes
 * through — so a query, a mutation, a blob fetch and the retry-after-refresh
 * path all report it without any of them knowing the code exists.
 */
export type PremisesRestrictedListener = () => void;
let onPremisesRestricted: PremisesRestrictedListener | null = null;

export function setPremisesRestrictedListener(
  listener: PremisesRestrictedListener | null,
): void {
  onPremisesRestricted = listener;
}

/**
 * Design U8 item 11 (`docs/ANGOLA-AGT-VALIDATION-DESIGN.md` §3, §5 D16). Fired when ANY
 * response comes back 403 `PASSWORD_CHANGE_REQUIRED`: the session was minted with the
 * must-change claim (the password is older than the ruled maximum, or an administrator
 * typed it) and the API admits only `PATCH /me/password`, `POST /auth/logout` and
 * `GET /me` until it is changed.
 *
 * Same shape as the two listeners above, for the same reason: this is a whole-session
 * state, not a query's. The FIRST such 403 means every other request will answer the same
 * way, so the session provider swaps the app for the password screen once rather than
 * every screen showing a generic refusal.
 *
 * ⚠ IT IS NOT A 401, AND IT MUST NOT ENTER THE REFRESH PATH. A refresh would mint another
 * token carrying the same claim — a loop, not a fix. `apiRequest` only refreshes on 401;
 * this fires from `toResult`, where a 403 is already a final answer.
 *
 * The ordinary way the provider learns of the claim is `GET /me`'s
 * `must_change_password` (the token's own claim, echoed): the listener covers the
 * session that was minted with it BEFORE this page loaded `/me` — a refresh in another
 * tab, an expiry that crossed the 90-day line mid-session.
 */
export type PasswordChangeRequiredListener = () => void;
let onPasswordChangeRequired: PasswordChangeRequiredListener | null = null;

export function setPasswordChangeRequiredListener(
  listener: PasswordChangeRequiredListener | null,
): void {
  onPasswordChangeRequired = listener;
}

function readRefreshToken(): string | null {
  try {
    return window.localStorage.getItem(REFRESH_STORAGE_KEY);
  } catch {
    // Private-mode / blocked storage. Treated as "no session", never as an error:
    // the user can still sign in, they just will not survive a reload.
    return null;
  }
}

function writeRefreshToken(token: string | null): void {
  try {
    if (token === null) window.localStorage.removeItem(REFRESH_STORAGE_KEY);
    else window.localStorage.setItem(REFRESH_STORAGE_KEY, token);
  } catch {
    /* see readRefreshToken */
  }
}

export interface TokenPair {
  access: string;
  refresh: string;
}

/** Adopt a fresh pair (login, or a completed refresh). Bumps the generation. */
export function setTokens(pair: TokenPair): void {
  accessToken = pair.access;
  writeRefreshToken(pair.refresh);
  tokenGeneration += 1;
}

/** Drop everything. Called on logout and on a failed refresh. */
export function clearTokens(): void {
  accessToken = null;
  writeRefreshToken(null);
  tokenGeneration += 1;
}

export function hasStoredSession(): boolean {
  return readRefreshToken() !== null;
}

export function currentAccessToken(): string | null {
  return accessToken;
}

/**
 * Test seam. Resets every module-level variable so one test's mutex state cannot
 * leak into the next. Exported rather than reached into, so the invariants above
 * stay the only way to mutate this state in production code.
 */
export function __resetApiClientForTests(): void {
  accessToken = null;
  tokenGeneration = 0;
  refreshInFlight = null;
  onSessionExpired = null;
  onPremisesRestricted = null;
  onPasswordChangeRequired = null;
  writeRefreshToken(null);
}

/* ──────────────────────────────────────────────────────────────── the wire ── */

function url(path: string): string {
  return path.startsWith('http') ? path : `${API_BASE_URL}${path}`;
}

async function parseBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text === '') return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

async function rawRequest(
  path: string,
  init: RequestInit,
  bearer: string | null,
): Promise<Response> {
  const headers = new Headers(init.headers);
  /* A `FormData` body must NOT get a Content-Type here: the browser writes
   * `multipart/form-data` WITH its boundary parameter, and a hand-set value
   * would ship a body the server cannot split. Everything else this client
   * sends is JSON. */
  if (
    init.body !== undefined &&
    init.body !== null &&
    !(init.body instanceof FormData) &&
    !headers.has('Content-Type')
  ) {
    headers.set('Content-Type', 'application/json');
  }
  headers.set('Accept', 'application/json');
  if (bearer !== null) headers.set('Authorization', `Bearer ${bearer}`);

  try {
    return await fetch(url(path), { ...init, headers });
  } catch (cause) {
    throw new ApiTransportError(
      'The server could not be reached. Check that the API is running.',
      cause,
    );
  }
}

async function toResult<T>(response: Response): Promise<T> {
  const body = await parseBody(response);
  if (response.ok) return body as T;
  if (isApiErrorEnvelope(body)) {
    // D7: a premises refusal is session-wide, not query-wide. Notify BEFORE
    // throwing so the blocked screen mounts even if the caller swallows the
    // error (the sign-out path does, deliberately).
    if (response.status === 403 && body.error.code === 'PREMISES_RESTRICTED') {
      onPremisesRestricted?.();
    }
    // Design U8 / D16: the must-change refusal is session-wide too, and notified here for
    // the same reason — before throwing, from the one funnel every response passes through.
    if (response.status === 403 && body.error.code === 'PASSWORD_CHANGE_REQUIRED') {
      onPasswordChangeRequired?.();
    }
    throw new ApiRequestError(
      response.status,
      body.error.code,
      body.error.message,
      body.error.details,
    );
  }
  // Not the spec envelope — a proxy error page, a 502, a helmet refusal. Surface
  // the status honestly rather than inventing a code the client might branch on.
  throw new ApiRequestError(
    response.status,
    'HTTP_' + String(response.status),
    typeof body === 'string' && body !== ''
      ? body
      : `The server answered ${String(response.status)}.`,
  );
}

/* ───────────────────────────────────────────────── THE SINGLE-FLIGHT REFRESH ── */

/**
 * Ensure the access token is newer than `sentUnderGeneration`, refreshing at most
 * once across all concurrent callers.
 *
 * Returns the access token to retry with. Throws if the chain is gone — at which
 * point the session is already cleared and the listener has fired.
 */
async function ensureRefreshed(sentUnderGeneration: number): Promise<string> {
  // (a) Somebody already rotated while this request was in flight. Do NOT refresh
  //     again — presenting the rotated token is what revokes the chain.
  if (tokenGeneration > sentUnderGeneration && accessToken !== null) {
    return accessToken;
  }

  // (b) A refresh is already outstanding: join it.
  if (refreshInFlight !== null) {
    return refreshInFlight;
  }

  // (c) We are the one who refreshes.
  const stored = readRefreshToken();
  if (stored === null) {
    clearTokens();
    onSessionExpired?.();
    throw new ApiRequestError(401, 'TOKEN_INVALID', 'Your session has ended. Please sign in.');
  }

  refreshInFlight = (async (): Promise<string> => {
    const response = await rawRequest(
      '/auth/refresh',
      { method: 'POST', body: JSON.stringify({ refresh: stored }) },
      null,
    );
    const pair = await toResult<TokenPair>(response);
    setTokens(pair);
    return pair.access;
  })();

  try {
    return await refreshInFlight;
  } catch (error) {
    clearTokens();
    onSessionExpired?.();
    throw error;
  } finally {
    // Cleared in `finally` so a failed refresh does not wedge the mutex closed for
    // the rest of the page's life.
    refreshInFlight = null;
  }
}

/* ─────────────────────────────────────────────────────────── public surface ── */

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Query parameters. `undefined` and `null` values are dropped, not sent blank. */
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  /**
   * Skip the 401→refresh→retry path. Used by `/auth/login` and `/auth/refresh`
   * themselves: a 401 from those IS the answer, not a stale-token symptom, and
   * retrying a bad password through the refresh machinery would be nonsense.
   */
  anonymous?: boolean;
}

function withQuery(path: string, query: RequestOptions['query']): string {
  if (query === undefined) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs === '' ? path : `${path}?${qs}`;
}

/**
 * The one way this app talks to the API.
 *
 * ESLint forbids `fetch` everywhere but this module (`eslint.config.js`), because
 * a raw fetch is a request that does not participate in the mutex above — and one
 * of those, firing its own refresh, is enough to trip revoke-on-rotate.
 */
export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, signal, anonymous = false } = options;
  const init: RequestInit = { method };
  // FormData rides as-is (multipart, boundary set by the browser — see
  // `rawRequest`); anything else is JSON. Re-sending the same FormData on the
  // 401→refresh→retry path below is safe: `fetch` serialises it per request.
  if (body !== undefined) init.body = body instanceof FormData ? body : JSON.stringify(body);
  if (signal !== undefined) init.signal = signal;

  const fullPath = withQuery(path, query);

  if (anonymous) {
    return toResult<T>(await rawRequest(fullPath, init, null));
  }

  const sentUnderGeneration = tokenGeneration;
  const first = await rawRequest(fullPath, init, accessToken);
  if (first.status !== 401) {
    return toResult<T>(first);
  }

  // Drain the 401 body so the connection is not left half-read, then refresh once
  // (or await the refresh already running) and retry EXACTLY ONCE. A second 401
  // after a fresh token is a real refusal, not an expiry.
  await parseBody(first);
  const retryToken = await ensureRefreshed(sentUnderGeneration);
  const second = await rawRequest(fullPath, init, retryToken);
  return toResult<T>(second);
}

/* ────────────────────────────────────────────────────────────── auth calls ── */

export interface MeResponse {
  id: number;
  name: string;
  email: string;
  phone: string | null;
  status: 'active' | 'disabled';
  is_group_admin: boolean;
  last_login_at: string | null;
  grp: boolean;
  scopes: Array<{ c: string; r: string }>;
  perms: Record<string, string[]>;
  /**
   * THIS SESSION must change its password before it can do anything else — the token's
   * `mc` claim, echoed by `GET /me` (design U8 item 11, D16). The session provider renders
   * the password screen instead of the app while it is true; the web never decodes the JWT.
   */
  must_change_password: boolean;
}

export async function login(email: string, password: string): Promise<TokenPair> {
  const pair = await apiRequest<TokenPair>('/auth/login', {
    method: 'POST',
    body: { email, password },
    anonymous: true,
  });
  setTokens(pair);
  return pair;
}

/**
 * Rebuild a session from the stored refresh token (page load / reload).
 *
 * Routed through `ensureRefreshed` rather than posting directly, so the boot
 * refresh obeys the same mutex as every other one. React StrictMode mounts the
 * session provider's effect twice in development; without this, the second mount
 * would present the token the first mount just rotated — and log the developer
 * out on every reload.
 */
export async function restoreSession(): Promise<void> {
  // Already holding a live access token: there is nothing to restore, and asking
  // for one anyway would burn a rotation for no reason.
  if (accessToken !== null) return;
  await ensureRefreshed(tokenGeneration);
}

export async function fetchMe(): Promise<MeResponse> {
  return apiRequest<MeResponse>('/me');
}

export async function logout(): Promise<void> {
  const refresh = readRefreshToken();
  try {
    if (refresh !== null) {
      await apiRequest<{ revoked: number }>('/auth/logout', {
        method: 'POST',
        body: { refresh },
      });
    }
  } catch {
    // A logout that cannot reach the server still ends the local session. Failing
    // to clear local tokens because the network blipped would leave the user
    // apparently signed in.
  } finally {
    clearTokens();
  }
}

/* ────────────────────────────────────────────────────────────────── avatars ── */

/** What `PUT /me/avatar` answers (`AvatarUpdatedView`, avatars.service.ts). */
export interface AvatarUpdated {
  ok: true;
  /** API-relative, e.g. `/users/7/avatar` — the path `fetchAvatarBlob` reads. */
  avatar_url: string;
}

/** What `DELETE /me/avatar` answers. `removed:false` = idempotent second click. */
export interface AvatarRemoved {
  ok: true;
  removed: boolean;
}

/**
 * `GET /users/:id/avatar` — the photo bytes, or `null` when the user has none.
 *
 * This cannot be an `<img src>` pointing at the API: the route is auth-checked
 * by the JWT guard and an image element cannot carry an `Authorization` header.
 * So the bytes come through this client — which also means the request obeys
 * the same single-flight refresh discipline as everything else (D-web-1) — and
 * the caller turns them into an object URL for the `Avatar` component's `src`.
 *
 * `cache: 'no-cache'` is the cache-busting half of the contract: the server
 * answers `Cache-Control: private, max-age=60` with a strong content-hash ETag,
 * and without this flag the browser would serve a just-replaced photo from its
 * HTTP cache for up to a minute. `no-cache` forces revalidation on every fetch —
 * an unchanged photo costs a 304 (fetch transparently returns the cached bytes),
 * a replaced one streams fresh. Render-time economy is React Query's job
 * (`useAvatarUrl` holds the blob for its staleTime), not the HTTP cache's.
 *
 * A 404 is an ANSWER — "this user has no photo" — not an error, so it returns
 * `null` rather than throwing: the Avatar contract is 404 → initials fallback,
 * and an exception here would put every photo-less user's disc into an error
 * state instead.
 */
export async function fetchAvatarBlob(userId: string | number): Promise<Blob | null> {
  const path = `/users/${encodeURIComponent(String(userId))}/avatar`;
  const init: RequestInit = { method: 'GET', cache: 'no-cache' };

  const sentUnderGeneration = tokenGeneration;
  let response = await rawRequest(path, init, accessToken);
  if (response.status === 401) {
    // Same drain→refresh-once→retry-once shape as `apiRequest`; see there.
    await parseBody(response);
    const retryToken = await ensureRefreshed(sentUnderGeneration);
    response = await rawRequest(path, init, retryToken);
  }

  if (response.status === 404) {
    await parseBody(response);
    return null;
  }
  if (!response.ok) return toResult<never>(response);
  return response.blob();
}

/* ─────────────────────────────────────────────────────── auth-checked files ── */

/** A downloaded file: the bytes, plus the name the server filed it under. */
export interface BinaryResponse {
  blob: Blob;
  /**
   * From `Content-Disposition: attachment; filename="…"`. `null` when the server
   * sent none — the caller then supplies its own name rather than inventing one
   * from the URL, which would leak the storage path into the user's downloads.
   */
  filename: string | null;
}

/**
 * `GET <path>` for a route that answers BYTES behind the JWT guard — Phase 3a's
 * `GET /quotes/:id/pdf`.
 *
 * A plain `<a href>` or `window.open` CANNOT reach these routes: neither carries
 * an `Authorization` header, so both get 401. Same constraint, same solution, as
 * {@link fetchAvatarBlob} — the bytes come through this client, so the request
 * also obeys the single-flight refresh discipline (D-web-1), and the caller turns
 * them into an object URL.
 *
 * GENERIC WHERE `fetchAvatarBlob` IS NOT, DELIBERATELY. That function maps 404 →
 * `null`, because "this user has no photo" is an ANSWER for an avatar. It is not
 * an answer for a document: a 404 from `GET /quotes/:id/pdf` means the quote has
 * no rendered PDF (or the render was invalidated by a re-price), and a caller
 * that received `null` would have to guess which. So this throws the ordinary
 * `ApiRequestError` and the caller branches on the code.
 *
 * `cache: 'no-cache'` for the reason the avatar path documents: the PDF route
 * answers a strong content-hash ETag, and a re-render must not be served from
 * the HTTP cache. An unchanged document still costs only a 304.
 *
 * With a `body` it POSTs that body as JSON instead — for a route that PRODUCES its file on
 * request, the SAF-T (AO) export (`POST /countries/:cc/saft-ao/export`, design §3 U6).
 */
export async function fetchBinary(path: string, body?: unknown): Promise<BinaryResponse> {
  const init: RequestInit =
    body === undefined
      ? { method: 'GET', cache: 'no-cache' }
      : { method: 'POST', cache: 'no-store', body: JSON.stringify(body) };

  const sentUnderGeneration = tokenGeneration;
  let response = await rawRequest(path, init, accessToken);
  if (response.status === 401) {
    // Same drain→refresh-once→retry-once shape as `apiRequest`; see there.
    await parseBody(response);
    const retryToken = await ensureRefreshed(sentUnderGeneration);
    response = await rawRequest(path, init, retryToken);
  }

  if (!response.ok) return toResult<never>(response);
  return { blob: await response.blob(), filename: filenameFrom(response) };
}

/**
 * The `filename` parameter of a `Content-Disposition` header.
 *
 * Anything the header could carry is treated as UNTRUSTED TEXT: the value is
 * stripped of every path separator and control character before it can become a
 * download name, because a server (or a proxy) that answered
 * `filename="../../evil.exe"` must not be able to steer where the browser writes.
 * The API's own header is the document number, which survives this untouched.
 */
function filenameFrom(response: Response): string | null {
  const header = response.headers.get('Content-Disposition');
  if (header === null) return null;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
  const raw = match?.[1];
  if (raw === undefined) return null;
  let decoded = raw;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    /* not percent-encoded; use it as sent */
  }
  // Character-wise rather than a regex: a control-character CLASS written as a
  // literal trips `no-control-regex`, and disabling a lint rule in order to write
  // a security check is the wrong trade. Controls and path separators become "_".
  const safe = [...decoded]
    .map((char) => {
      // Compared as CODE POINTS, not as character literals: 0x2f is '/', 0x5c is
      // the path separator whose own escaping is the classic place a sanitiser
      // gets written wrong, and everything under 0x20 plus 0x7f is a control.
      const code = char.codePointAt(0) ?? 0;
      const unsafe = code < 0x20 || code === 0x7f || code === 0x2f || code === 0x5c;
      return unsafe ? '_' : char;
    })
    .join('')
    // A leading dot cannot start a name: '..' and '.hidden' are refused outright
    // rather than partially cleaned.
    .replace(/^[.]+/, '')
    .trim();
  return safe === '' ? null : safe;
}

/**
 * `PUT /me/avatar`. The server is the authority on type/size/content (magic-byte
 * sniff, 5MB cap, re-encode); the client-side mirror in `features/avatars`
 * exists only so an oversize pick fails before 5MB leave the machine.
 */
export async function uploadOwnAvatar(file: File): Promise<AvatarUpdated> {
  const form = new FormData();
  form.append('file', file);
  return apiRequest<AvatarUpdated>('/me/avatar', { method: 'PUT', body: form });
}

/** `DELETE /me/avatar` — idempotent on the server, so safe to double-click. */
export async function removeOwnAvatar(): Promise<AvatarRemoved> {
  return apiRequest<AvatarRemoved>('/me/avatar', { method: 'DELETE' });
}

/* ──────────────────────────────────────────────────────── SSE ticket + URL ── */

export interface SseTicket {
  ticket: string;
  expiresAt: string;
  ttlSeconds: number;
  /** Server-built, already contains the `/api/v1` prefix and the ticket query. */
  streamUrl: string;
  scope: { sub: string; grp: boolean; countries: string[]; globalFeed: boolean };
}

/**
 * Mint a stream ticket.
 *
 * ONE TICKET IS GOOD FOR ONE CONNECTION. The server burns the `jti` on redemption
 * (`sse-ticket.service.ts`), so a ticket recovered from a log is already spent —
 * and so is a ticket an `EventSource` tries to reuse. That is why every reconnect
 * mints a new one; see `useEventStream`.
 */
export async function mintSseTicket(): Promise<SseTicket> {
  return apiRequest<SseTicket>('/events/ticket', { method: 'POST' });
}

/**
 * Absolute URL for an SSE stream path.
 *
 * `streamUrl` comes back rooted at `/api/v1/...`, so it must be resolved against
 * the API's ORIGIN, not against `API_BASE_URL` (which already carries the
 * `/api/v1` prefix and would double it). When the base is a same-origin path the
 * page's own origin is the right one.
 */
export function absoluteStreamUrl(streamUrl: string): string {
  const origin = API_BASE_URL.startsWith('http')
    ? new URL(API_BASE_URL).origin
    : window.location.origin;
  return new URL(streamUrl, origin).toString();
}
